/**
 * Overview Tab Component
 *
 * Displays system summary, key statistics, and general information
 */

import React from 'react';
import {
  Server,
  Database,
  Code,
  Layers,
  FileText,
  Network,
  Calendar,
  Tag,
  Award,
  BookOpen,
  Target,
  Building2,
  Cloud,
  Grid3x3,
} from 'lucide-react';

interface OverviewTabProps {
  system: any;
  statistics: any;
}

const OverviewTab: React.FC<OverviewTabProps> = ({ system, statistics }) => {
  return (
    <div>
      <h2 className="text-2xl font-bold text-neutral-900 mb-6">System Overview</h2>

      {/* Basic Information */}
      <div className="bg-white border border-neutral-200 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-semibold text-neutral-900 mb-4">Basic Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-medium text-neutral-600 block mb-1">
              System Name
            </label>
            <p className="text-base text-neutral-900 font-medium">{system.system_name}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-neutral-600 block mb-1">Vendor</label>
            <p className="text-base text-neutral-900 font-medium">{system.vendor}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-neutral-600 block mb-1">Category</label>
            <div className="flex items-center gap-2">
              <Tag className="w-4 h-4 text-primary-600" />
              <p className="text-base text-neutral-900 font-medium">{system.category}</p>
            </div>
          </div>
          {system.version && (
            <div>
              <label className="text-sm font-medium text-neutral-600 block mb-1">Version</label>
              <p className="text-base text-neutral-900 font-medium">{system.version}</p>
            </div>
          )}
          {system.database_type && (
            <div>
              <label className="text-sm font-medium text-neutral-600 block mb-1">
                Database Type
              </label>
              <p className="text-base text-neutral-900 font-medium">{system.database_type}</p>
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-neutral-600 block mb-1">Status</label>
            <span
              className={`inline-block px-3 py-1 text-sm font-medium rounded-full ${
                system.status === 'published'
                  ? 'bg-green-100 text-green-800'
                  : system.status === 'researching'
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-yellow-100 text-yellow-800'
              }`}
            >
              {system.status}
            </span>
          </div>
        </div>
      </div>

      {/* System Overview (Phase 1 Research Data) */}
      {(system.description ||
        system.primary_use_cases?.length > 0 ||
        system.target_industries?.length > 0 ||
        system.typical_deployment ||
        system.estimated_modules) && (
        <div className="bg-white border border-neutral-200 rounded-lg p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <BookOpen className="w-6 h-6 text-primary-600" />
            <h3 className="text-lg font-semibold text-neutral-900">System Overview</h3>
          </div>

          <div className="space-y-6">
            {/* Description */}
            {system.description && (
              <div>
                <label className="text-sm font-medium text-neutral-600 block mb-2">
                  Description
                </label>
                <p className="text-base text-neutral-800 leading-relaxed">{system.description}</p>
              </div>
            )}

            {/* Two Column Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Primary Use Cases */}
              {system.primary_use_cases && system.primary_use_cases.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="w-4 h-4 text-primary-600" />
                    <label className="text-sm font-medium text-neutral-600">
                      Primary Use Cases
                    </label>
                  </div>
                  <ul className="space-y-1.5">
                    {system.primary_use_cases.map((useCase: string, index: number) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-neutral-800">
                        <span className="text-primary-600 mt-0.5">•</span>
                        <span>{useCase}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Target Industries */}
              {system.target_industries && system.target_industries.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="w-4 h-4 text-primary-600" />
                    <label className="text-sm font-medium text-neutral-600">
                      Target Industries
                    </label>
                  </div>
                  <ul className="space-y-1.5">
                    {system.target_industries.map((industry: string, index: number) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-neutral-800">
                        <span className="text-primary-600 mt-0.5">•</span>
                        <span>{industry}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Typical Deployment */}
              {system.typical_deployment && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Cloud className="w-4 h-4 text-primary-600" />
                    <label className="text-sm font-medium text-neutral-600">
                      Typical Deployment
                    </label>
                  </div>
                  <p className="text-base text-neutral-800 font-medium">
                    {system.typical_deployment}
                  </p>
                </div>
              )}

              {/* Estimated Modules */}
              {system.estimated_modules && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Grid3x3 className="w-4 h-4 text-primary-600" />
                    <label className="text-sm font-medium text-neutral-600">
                      Estimated Modules
                    </label>
                  </div>
                  <p className="text-base text-neutral-800 font-medium">
                    {system.estimated_modules} modules
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Statistics Grid */}
      <div className="bg-white border border-neutral-200 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-semibold text-neutral-900 mb-4">Discovered Intelligence</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-purple-100 rounded-lg mb-3">
              <Layers className="w-6 h-6 text-purple-600" />
            </div>
            <p className="text-2xl font-bold text-neutral-900">{statistics?.modules || 0}</p>
            <p className="text-sm text-neutral-600 mt-1">Modules</p>
          </div>
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg mb-3">
              <Database className="w-6 h-6 text-blue-600" />
            </div>
            <p className="text-2xl font-bold text-neutral-900">{statistics?.tables || 0}</p>
            <p className="text-sm text-neutral-600 mt-1">Tables</p>
          </div>
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg mb-3">
              <Code className="w-6 h-6 text-green-600" />
            </div>
            <p className="text-2xl font-bold text-neutral-900">{statistics?.api_endpoints || 0}</p>
            <p className="text-sm text-neutral-600 mt-1">APIs</p>
          </div>
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-yellow-100 rounded-lg mb-3">
              <Network className="w-6 h-6 text-yellow-600" />
            </div>
            <p className="text-2xl font-bold text-neutral-900">
              {statistics?.integration_patterns || 0}
            </p>
            <p className="text-sm text-neutral-600 mt-1">Patterns</p>
          </div>
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-orange-100 rounded-lg mb-3">
              <FileText className="w-6 h-6 text-orange-600" />
            </div>
            <p className="text-2xl font-bold text-neutral-900">
              {statistics?.documentation_links || 0}
            </p>
            <p className="text-sm text-neutral-600 mt-1">Docs</p>
          </div>
        </div>
      </div>

      {/* Quality & Research Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Quality Score */}
        {statistics?.quality_score !== undefined && (
          <div className="bg-white border border-neutral-200 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <Award className="w-6 h-6 text-primary-600" />
              <h3 className="text-lg font-semibold text-neutral-900">Quality Assessment</h3>
            </div>
            <div className="flex items-baseline gap-2 mb-3">
              <p className="text-4xl font-bold text-primary-600">
                {Math.round(statistics.quality_score)}
              </p>
              <p className="text-sm text-neutral-500">/ 100</p>
            </div>
            <div className="w-full bg-neutral-200 rounded-full h-2 mb-2">
              <div
                className={`h-2 rounded-full ${
                  statistics.quality_score >= 80
                    ? 'bg-green-500'
                    : statistics.quality_score >= 60
                    ? 'bg-yellow-500'
                    : 'bg-red-500'
                }`}
                style={{ width: `${statistics.quality_score}%` }}
              />
            </div>
            {statistics.quality_grade && (
              <p className="text-sm text-neutral-600">Grade: {statistics.quality_grade}</p>
            )}
          </div>
        )}

        {/* Research Info */}
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <Server className="w-6 h-6 text-primary-600" />
            <h3 className="text-lg font-semibold text-neutral-900">Research Status</h3>
          </div>
          <div className="space-y-3">
            {system.last_researched && (
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4 text-neutral-500" />
                <span className="text-neutral-600">Last Researched:</span>
                <span className="font-medium text-neutral-900">
                  {new Date(system.last_researched).toLocaleDateString()}
                </span>
              </div>
            )}
            {system.research_version && (
              <div className="flex items-center gap-2 text-sm">
                <Tag className="w-4 h-4 text-neutral-500" />
                <span className="text-neutral-600">Research Version:</span>
                <span className="font-medium text-neutral-900">v{system.research_version}</span>
              </div>
            )}
            {statistics?.last_assessed && (
              <div className="flex items-center gap-2 text-sm">
                <Award className="w-4 h-4 text-neutral-500" />
                <span className="text-neutral-600">Last Assessed:</span>
                <span className="font-medium text-neutral-900">
                  {new Date(statistics.last_assessed).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;
